import { useCallback, useMemo, useRef, useState } from 'react';

export type DropzoneErrorKind = 'count' | 'size' | 'type';

export interface DropzoneErrorContext {
  name?: string;
  count?: number;
  maxSizeMb?: number;
  accept?: string;
}

interface UseFileDropzoneOptions {
  onFilesSelected: (files: File[]) => void;
  accept: string[];
  maxFileSize: number;
  maxFiles: number;
  /** Kept outside the hook so the copy stays with the translations. */
  describeError: (kind: DropzoneErrorKind, context: DropzoneErrorContext) => string;
}

const extensionOf = (name: string) => `.${name.split('.').pop()?.toLowerCase() ?? ''}`;

/**
 * Native HTML5 file drop; no dependency needed. Drag libraries such as dnd-kit
 * solve pointer-based reordering of elements, not file transfer.
 */
export function useFileDropzone({
  onFilesSelected,
  accept,
  maxFileSize,
  maxFiles,
  describeError,
}: UseFileDropzoneOptions) {
  const [isDragActive, setIsDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // dragenter/leave fire per element, so the zone would flicker as the pointer
  // crosses its own icon or label. Depth tracking keeps it stable: enter and
  // leave are paired 1:1, which is why only those two move the counter.
  const dragDepth = useRef(0);

  const acceptSet = useMemo(() => accept.map((entry) => entry.toLowerCase()), [accept]);

  const accepted = useCallback(
    (files: File[]): { files: File[]; message: string | null } => {
      if (files.length > maxFiles) {
        return { files: [], message: describeError('count', { count: maxFiles }) };
      }

      const problems: string[] = [];
      const valid = files.filter((file) => {
        if (file.size > maxFileSize) {
          problems.push(
            describeError('size', {
              name: file.name,
              maxSizeMb: Math.round(maxFileSize / 1024 / 1024),
            }),
          );
          return false;
        }
        if (!acceptSet.includes(extensionOf(file.name))) {
          problems.push(describeError('type', { name: file.name, accept: accept.join(', ') }));
          return false;
        }
        return true;
      });

      return { files: valid, message: problems.length > 0 ? problems.join('; ') : null };
    },
    [accept, acceptSet, describeError, maxFileSize, maxFiles],
  );

  const handle = useCallback(
    (files: File[]) => {
      const result = accepted(files);
      setError(result.message);
      if (result.message === null && result.files.length > 0) onFilesSelected(result.files);
    },
    [accepted, onFilesSelected],
  );

  const dropzoneProps = useMemo(
    () => ({
      onDragEnter: (event: React.DragEvent) => {
        event.preventDefault();
        dragDepth.current += 1;
        setIsDragActive(true);
      },
      // dragover repeats for as long as the pointer hovers, so it must never
      // touch the depth counter — it only cancels the event, which is what
      // tells the browser this zone accepts the drop.
      onDragOver: (event: React.DragEvent) => {
        event.preventDefault();
      },
      onDragLeave: (event: React.DragEvent) => {
        event.preventDefault();
        dragDepth.current = Math.max(0, dragDepth.current - 1);
        if (dragDepth.current === 0) setIsDragActive(false);
      },
      onDrop: (event: React.DragEvent) => {
        event.preventDefault();
        dragDepth.current = 0;
        setIsDragActive(false);
        handle(Array.from(event.dataTransfer.files));
      },
    }),
    [handle],
  );

  const inputProps = useMemo(
    () => ({
      ref: inputRef,
      type: 'file' as const,
      accept: accept.join(','),
      multiple: maxFiles > 1,
      onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
        handle(Array.from(event.target.files ?? []));
        // Reset so choosing the same file twice still fires a change.
        event.target.value = '';
      },
    }),
    [accept, handle, maxFiles],
  );

  return {
    isDragActive,
    error,
    dropzoneProps,
    inputProps,
    browse: () => inputRef.current?.click(),
  };
}
